// Kompakte Übersicht fürs Dashboard: sendet jeder Kanal, wie viele hören zu, laufen die Server?

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { EngineBadge, StatusDot, errorText } from "./streaming-ui";
import { EqBars, LiveDot, Skeleton } from "./ui";
import type { EngineOverviewChannel, StreamingServer } from "../types";

export function StreamingStatusPanel({ refreshInterval = 10000 }: { refreshInterval?: number }): JSX.Element {
  const [channels, setChannels] = useState<EngineOverviewChannel[] | null>(null);
  const [servers, setServers] = useState<StreamingServer[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const tick = async (): Promise<void> => {
      try {
        const [overview, srv] = await Promise.all([api.getEngineOverview(), api.listServers()]);
        if (!active) return;
        setChannels(overview.channels);
        setServers(srv);
        setError(null);
      } catch (err) {
        if (active) setError(errorText(err));
      }
    };
    void tick();
    const t = setInterval(() => void tick(), refreshInterval);
    return () => {
      active = false;
      clearInterval(t);
    };
  }, [refreshInterval]);

  const listeners = servers.reduce((sum, s) => sum + (s.status?.listeners ?? 0), 0);
  const senden = channels ? channels.filter((c) => c.engine?.state === "running").length : null;
  const aktiv = channels ? channels.filter((c) => c.status === "active").length : null;
  const live = (channels ?? []).some((c) => c.now_playing?.on_air === "live");

  // Die Heldenkarte des Dashboards: links die große Zahl (wer hört zu),
  // rechts jeder Kanal als Zeile, unten die Server als Chips.
  return (
    <section className="card relative overflow-hidden" aria-labelledby="auf-sendung">
      <div className="brand-glow pointer-events-none absolute inset-0" aria-hidden="true" />
      <div className="relative">
        <div className="flex flex-wrap items-center justify-between gap-2 px-5 pt-4 sm:px-6">
          <h2 id="auf-sendung" className="flex items-center gap-2 text-[0.95rem] font-semibold text-primary">
            {live ? <LiveDot /> : senden ? <EqBars className="text-brand-primary" /> : <StatusDot tone="idle" />}
            Auf Sendung
          </h2>
          <Link to="/servers" className="btn btn-ghost btn-sm -mr-2">Server & Sendeabwicklung →</Link>
        </div>

        {error && <div className="callout callout-danger mx-5 mt-3 text-xs sm:mx-6">{error}</div>}

        <div className="grid gap-6 px-5 pb-5 pt-4 sm:px-6 lg:grid-cols-[minmax(0,240px)_minmax(0,1fr)] lg:gap-8">
          {/* Die große Zahl: wer gerade zuhört. */}
          <div className="flex flex-col justify-between gap-4">
            <div>
              <div className="text-[0.8rem] font-medium text-muted">Hörer gesamt</div>
              <div className="mt-1 text-5xl font-semibold leading-none tracking-tight text-primary tabular-nums sm:text-6xl">{listeners}</div>
              <div className="mt-2 text-xs text-muted">gleichzeitig, über alle Server</div>
            </div>
            <div>
              <div className="flex items-baseline justify-between text-[0.8rem]">
                <span className="text-muted">Kanäle, die senden</span>
                <span className="font-semibold tabular-nums text-primary">
                  {senden ?? "—"}
                  <span className="font-normal text-muted"> / {aktiv ?? "—"}</span>
                </span>
              </div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-input" role="presentation">
                <div
                  className="h-full rounded-full bg-brand-primary transition-all"
                  style={{ width: `${senden && aktiv ? Math.round((senden / aktiv) * 100) : 0}%` }}
                />
              </div>
            </div>
          </div>

          {/* Jeder Kanal eine Zeile: Name, was läuft, Zustand. */}
          <ul className="divide-y divide-line overflow-hidden rounded-lg border border-default">
            {!channels && [0, 1].map((i) => (
              <li key={i} className="px-4 py-3"><Skeleton className="h-5 w-2/3" /></li>
            ))}
            {channels && channels.length === 0 && (
              <li className="px-4 py-6 text-center text-sm text-muted">Noch kein Kanal angelegt.</li>
            )}
            {(channels ?? []).map((c) => {
              const titel = c.now_playing && (c.now_playing.title || c.now_playing.artist)
                ? [c.now_playing.artist, c.now_playing.title].filter(Boolean).join(" – ")
                : null;
              return (
                <li key={c.id} className="flex min-w-0 items-center justify-between gap-3 px-4 py-2.5 transition hover:bg-surface-hover">
                  <div className="min-w-0">
                    <Link to={`/channels/${c.slug}`} className="block truncate text-sm font-medium text-primary hover:underline">{c.name}</Link>
                    {titel && <div className="truncate text-xs text-muted">{titel}</div>}
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-2.5">
                    {c.now_playing?.on_air === "live" && (
                      <span className="badge badge-danger font-semibold"><LiveDot className="h-1.5 w-1.5" />LIVE</span>
                    )}
                    <EngineBadge state={c.engine?.state ?? null} active={c.status === "active"} />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {servers.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-t border-default px-5 py-3 sm:px-6">
            <span className="mr-1 text-xs text-muted">Server</span>
            {servers.map((s) => (
              <span
                key={s.id}
                className="inline-flex items-center gap-1.5 rounded-full border border-default bg-card px-2.5 py-1 text-xs text-secondary"
                title={s.status?.error}
              >
                <StatusDot tone={s.status?.reachable ? "ok" : "error"} />
                {s.name}
                {s.status?.reachable && <span className="tabular-nums text-muted">· {s.status.listeners}</span>}
              </span>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
